import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type ColorScheme = 'light' | 'dark' | 'system';
export type ResolvedColorScheme = 'light' | 'dark';

type ContextValue = {
  colorScheme: ColorScheme;
  resolvedScheme: ResolvedColorScheme;
  toggleColorScheme: (value?: ColorScheme) => void;
};

const AppColorSchemeContext = createContext<ContextValue | undefined>(undefined);

function getSystemScheme(): ResolvedColorScheme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}

export function AppColorSchemeProvider({ children }: { children: React.ReactNode }) {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    try {
      return (localStorage.getItem('mk-admin-color-scheme') as ColorScheme) || 'system';
    } catch {
      return 'system';
    }
  });

  const [systemScheme, setSystemScheme] = useState<ResolvedColorScheme>(getSystemScheme);

  // Track OS-level preference changes
  useEffect(() => {
    let mq: MediaQueryList | null = null;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const handler = () => setSystemScheme(getSystemScheme());
    mq.addEventListener('change', handler);
    return () => mq?.removeEventListener('change', handler);
  }, []);

  const resolvedScheme: ResolvedColorScheme =
    colorScheme === 'system' ? systemScheme : colorScheme;

  useEffect(() => {
    try {
      if (resolvedScheme === 'light') document.documentElement.classList.add('light');
      else document.documentElement.classList.remove('light');
      localStorage.setItem('mk-admin-color-scheme', colorScheme);
    } catch {
      // ignore
    }
  }, [colorScheme, resolvedScheme]);

  const toggleColorScheme = useCallback((value?: ColorScheme) => {
    setColorScheme((current) => {
      if (value !== undefined) return value;
      // Cycle: system → light → dark → system
      if (current === 'system') return 'light';
      if (current === 'light') return 'dark';
      return 'system';
    });
  }, []);

  return (
    <AppColorSchemeContext.Provider value={{ colorScheme, resolvedScheme, toggleColorScheme }}>
      {children}
    </AppColorSchemeContext.Provider>
  );
}

export function useAppColorScheme() {
  const ctx = useContext(AppColorSchemeContext);
  if (!ctx) throw new Error('useAppColorScheme must be used within AppColorSchemeProvider');
  return ctx;
}
